import type { MigrationResponse, MigrationContentItem, CreateMigrateParams } from "./types";
import axios from "axios";
import api from "../api/axios";
import { ENDPOINTS } from "../api/endpoints";

/**
 * Asegura que una cadena base64 tenga el padding correcto
 *
 * @param base64 Cadena base64 a verificar
 * @returns Cadena base64 con el padding correcto
 */
function ensureBase64Padding(base64: string): string {
  const remainder = base64.length % 4;
  if (remainder > 0) {
    return base64 + "=".repeat(4 - remainder);
  }
  return base64;
}

/**
 * Realiza una solicitud de migración de especificación de API
 *
 * @param params Parámetros para la migración
 * @param headers Headers requeridos para la solicitud
 * @returns Respuesta de la migración
 */
export async function migrationCreate(params: CreateMigrateParams, headers: any): Promise<MigrationResponse> {
  try {
    // Verificar que el contenido tenga el formato de URI de datos correcto
    if (!params.content.startsWith("data:")) {
      // Intentar corregirlo si es posible
      if (params.content.includes(",")) {
        const base64Part = params.content.split(",")[1];
        const paddedBase64 = ensureBase64Padding(base64Part);
        params.content = `data:@file/yaml;base64,${paddedBase64}`;
      } else {
        const paddedBase64 = ensureBase64Padding(params.content);
        params.content = `data:@file/yaml;base64,${paddedBase64}`;
      }
    } else if (
      !params.content.startsWith("data:@file/yaml;base64,") &&
      !params.content.startsWith("data:application/zip;base64,")
    ) {
      // Detectar si es un ZIP o YAML basado en el prefijo
      const isZip =
        params.content.startsWith("data:application/zip") ||
        params.content.startsWith("data:application/x-zip") ||
        params.content.includes("zip");

      const parts = params.content.split(",");
      if (parts.length > 1) {
        const base64Part = parts[1];
        const paddedBase64 = ensureBase64Padding(base64Part);
        if (isZip) {
          params.content = `data:application/zip;base64,${paddedBase64}`;
        } else {
          params.content = `data:@file/yaml;base64,${paddedBase64}`;
        }
      }
    } else {
      const parts = params.content.split(",");
      if (parts.length > 1) {
        const base64Part = parts[1];
        const paddedBase64 = ensureBase64Padding(base64Part);
        // Mantener el mismo prefijo MIME que ya tiene
        const mimePrefix = parts[0];
        params.content = `${mimePrefix},${paddedBase64}`;
      }
    }

    // Verificar si el contenido termina en == para debugging
    // const base64Part = params.content.split(",")[1] || "";

    const requestBody = {
      content: params.content, // URI de datos completo
      migrationName: params.migrationName,
      originSpecType: params.originSpecType,
      destinationSpecType: params.destinationSpecType,
      prompt: params.prompt,
      uploadToSource: params.uploadToSource,
      sourceFileName: params.sourceFileName,
    };

    // Configurar los headers requeridos
    const requestHeaders = {
      ...headers,
    };

    // Añadir un timeout más largo para dar tiempo a la API a procesar
    const responseCreateMigrate = await api
      .post(ENDPOINTS.MIGRATION.CREATE, requestBody, {
        headers: requestHeaders,
        timeout: 120000, // 120 segundos (2 minutos)
      })
      .then((res) => {
        return res.data.content;
      });

    // Inicializar respuesta de migración
    const migrationResponse: MigrationResponse = {
      success: true,
      status: 200,
      content: {},
      comments: responseCreateMigrate.content?.[0]?.comments || [],
    };

    let objApiList: any;
    let arrAppis: any = [];
    responseCreateMigrate.migrationResult.forEach((el: { id: any }) => {
      arrAppis.push(el.id);
    });
    const bodyAssessment = {
      migrationId: responseCreateMigrate.migrationId,
      appis: arrAppis,
    };

    const responseAssessment = await api
      .post(ENDPOINTS.ASSESSMENT.CREATE, bodyAssessment, {
        headers: requestHeaders,
        timeout: 120000, // 120 segundos (2 minutos)
      })
      .then((res) => {
        return res.data.content;
      });

    objApiList = await responseAssessment.assessmentResult.forEach((el: any) => {
      responseCreateMigrate.migrationResult.forEach((elMigrate: any) => {
        if (elMigrate.id === el.appId) {
          el["apiName"] = elMigrate.apiName;
          el["candidate"] = "Yes";
          el["sourceFileName"] = responseCreateMigrate.sourceFileName;
          el["excluded"] = elMigrate.excluded;
        }
      });
    });

    // Manejar la nueva estructura de respuesta donde content es un array
    if (responseCreateMigrate && Array.isArray(responseCreateMigrate) && responseCreateMigrate.length > 0) {
      const contentItem: MigrationContentItem = responseCreateMigrate[0];

      if (contentItem.fileContent) {
        const base64Content = contentItem.fileContent;
        const binaryContent = atob(base64Content);
        const bytes = new Uint8Array(binaryContent.length);

        for (let i = 0; i < binaryContent.length; i++) {
          bytes[i] = binaryContent.charCodeAt(i);
        }

        const blob = new Blob([bytes], { type: "application/zip" });

        const blobReader = new FileReader();
        blobReader.onload = () => {
          const firstBytes = new Uint8Array(blobReader.result as ArrayBuffer).slice(0, 4);
        };
        blobReader.readAsArrayBuffer(blob.slice(0, 4));

        migrationResponse.content = {
          description: contentItem.description,
          apiName: contentItem.apiName,
          link: contentItem.link,
          fileContent: base64Content,
          "application/zip": {
            data: blob,
          },
        };
      }
    } else if (responseCreateMigrate && !Array.isArray(responseCreateMigrate)) {
      // Manejar el caso en que content no sea un array
      migrationResponse.content = responseAssessment;
    }

    return migrationResponse;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorData = error.response?.data;
      return {
        success: false,
        status: error.response?.status || 500,
        error:
          errorData?.detail ||
          (errorData?.content && typeof errorData.content === "object"
            ? JSON.stringify(errorData.content)
            : errorData?.content) ||
          errorData?.message ||
          "Ocurrió un error durante la migración",
        content: errorData?.content || undefined,
        comments: [], // Añadir esta propiedad para satisfacer el tipo MigrationResponse
      };
    }

    return {
      success: false,
      status: 500,
      error: error instanceof Error ? error.message : "Ocurrió un error inesperado",
      content: {}, // Añadir esta propiedad para satisfacer el tipo MigrationResponse
      comments: [], // Añadir esta propiedad para satisfacer el tipo MigrationResponse
    };
  }
}
